import * as React from 'react'

import { useMutation, useQuery } from '@apollo/client'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarFile, F0Button, F0Card, F0TagStatus } from '@factorialco/f0-react'
import { Dropdown, OneModal, ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Ellipsis, EyeVisible, FileSigned } from '@factorialco/f0-react/icons/app'
import {
  EmployeeDetailsDocument,
  ItManagementValueObjectsItAssetDocumentTypeEnum,
  SendEmployeeDocumentTypeForSignatureItAssetDocumentDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DocumentModal from 'modules/documents/components/DocumentModal'
import { safeAssetTypeAsIcon } from 'modules/it_management/components/AssetsList/utils'
import { useGroupedDocumentsByEmployee } from 'modules/it_management/hooks/useGroupedDocumentsByEmployee'

type CompanyEmployee = {
  id: number
  firstName: string
  lastName: string
  employeeName: string
  email?: string
  isActive: boolean
  startDate: string | null
  workplace: string
  totalItAssets: number
  totalPendingSignatureDocuments: number
  access?: {
    id: number
    avatar?: string | null
  } | null
}

interface Props {
  employee: CompanyEmployee
  open: boolean
  onClose: () => void
}

const EmployeeDetailsSidePanel: React.FC<Props> = ({ employee, open, onClose }) => {
  const { company } = useCurrentSession()
  const [selectedDocumentId, setSelectedDocumentId] = React.useState<string | null>(null)
  const pollingDocumentIdsRef = React.useRef<Set<string>>(new Set())

  const { data, loading, startPolling, stopPolling } = useQuery(EmployeeDetailsDocument, {
    variables: { employeeId: employee.id },
    skip: !employee.id || !open,
  })

  const [sendEmployeeDocumentTypeForSignature] = useMutation(
    SendEmployeeDocumentTypeForSignatureItAssetDocumentDocument
  )

  const assets = data?.itManagement?.itAssetsConnection?.nodes || []

  const rawDocuments = React.useMemo(
    () => data?.itManagement?.itAssetDocumentsConnection?.edges?.map((edge) => edge.node) || [],
    [data?.itManagement?.itAssetDocumentsConnection?.edges]
  )

  const [localDocuments, setLocalDocuments] = React.useState(rawDocuments)

  React.useEffect(() => {
    const updatedDocs = rawDocuments.map((doc) => {
      if (pollingDocumentIdsRef.current.has(doc.id) && !doc.documentId) {
        return { ...doc, signatureStatus: 'pending' }
      }
      return doc
    })

    setLocalDocuments(updatedDocs)

    if (pollingDocumentIdsRef.current.size > 0) {
      pollingDocumentIdsRef.current.forEach((docId) => {
        const doc = rawDocuments.find((d) => d.id === docId)
        if (doc?.documentId) {
          pollingDocumentIdsRef.current.delete(docId)
        }
      })

      if (pollingDocumentIdsRef.current.size === 0) {
        stopPolling()
      }
    }
  }, [rawDocuments, stopPolling])

  const documents = useGroupedDocumentsByEmployee(localDocuments)

  const getSignatureStatus = (
    signatureStatus: string | null | undefined
  ): { text: string; variant: 'neutral' | 'warning' | 'positive' } => {
    switch (signatureStatus) {
      case 'draft':
        return {
          text: i18n.t('it_management.people_tab.employee_details.draft'),
          variant: 'neutral',
        }
      case 'pending':
        return {
          text: i18n.t('it_management.people_tab.employee_details.pending_signature'),
          variant: 'warning',
        }
      case 'signed':
        return {
          text: i18n.t('it_management.people_tab.employee_details.signed'),
          variant: 'positive',
        }
      default:
        return {
          text: i18n.t('it_management.people_tab.employee_details.draft'),
          variant: 'neutral',
        }
    }
  }

  const fullName = employee.employeeName || `${employee.firstName} ${employee.lastName}`
  const avatarUrl = employee.access?.avatar ? buildUrl(employee.access.avatar) : ''

  const isActive = employee.isActive

  const metadata: Array<{ label: string; value: { type: 'text'; content: string } }> = [
    employee.startDate && {
      label: i18n.t('it_management.people_tab.employee_details.start_date'),
      value: {
        type: 'text' as const,
        content: dateBackendDateTimeToUI(employee.startDate),
      },
    },
  ].filter((item): item is { label: string; value: { type: 'text'; content: string } } =>
    Boolean(item)
  )

  const handleClose = React.useCallback(() => {
    if (selectedDocumentId) {
      setSelectedDocumentId(null)
    } else {
      onClose()
    }
  }, [selectedDocumentId, onClose])

  return (
    <>
      {selectedDocumentId && (
        <DocumentModal
          documentId={selectedDocumentId}
          documents={[]}
          onClose={() => setSelectedDocumentId(null)}
        />
      )}

      <OneModal isOpen={open} onClose={handleClose} position="right">
        <OneModal.Header
          module={{
            id: 'it_management',
            label: i18n.t('it_management.it_inventory.title'),
            href: '/it-inventory',
          }}
        />
        <OneModal.Content withPadding>
          <ResourceHeader
            title={fullName}
            description={employee.email || ''}
            avatar={{
              type: 'person',
              firstName: employee.firstName,
              lastName: employee.lastName,
              src: avatarUrl || undefined,
            }}
            metadata={metadata}
            status={{
              label: i18n.t('it_management.people_tab.employee_details.status'),
              text: isActive
                ? i18n.t('it_management.people_tab.employee_details.active_employee')
                : i18n.t('it_management.people_tab.employee_details.inactive'),
              variant: isActive ? 'positive' : 'neutral',
            }}
          />

          <Box gap="s32" paddingTop="s32">
            {/* Devices Section */}
            <Box gap="s16">
              <Text size="300" weight="semibold" color="grey1000">
                {i18n.t('it_management.people_tab.employee_details.devices_assigned')}
              </Text>
              {loading ? (
                <Text.Normal color="grey600">{i18n.t('common.loading')}</Text.Normal>
              ) : assets.length > 0 ? (
                <Box gap="s12">
                  {assets.map((asset) => (
                    <F0Card
                      key={asset.id}
                      compact
                      avatar={{
                        type: 'icon',
                        icon: safeAssetTypeAsIcon(asset.itAssetModel?.typeName ?? ''),
                      }}
                      title={`${asset.itAssetModel?.brand} ${asset.itAssetModel?.name}`}
                    />
                  ))}
                </Box>
              ) : (
                <></>
              )}
            </Box>

            {/* Documents Section */}
            <Box gap="s16">
              <Text size="300" weight="semibold" color="grey1000">
                {i18n.t('it_management.people_tab.employee_details.it_documents')}
              </Text>
              {loading ? (
                <Text.Normal color="grey600">{i18n.t('common.loading')}</Text.Normal>
              ) : documents.length > 0 ? (
                <Box
                  borderRadius={{ all: 'abs008' }}
                  border={{ all: { color: 'grey300', width: 's1', style: 'solid' } }}
                >
                  {documents.map((document, index) => {
                    const signatureStatus = getSignatureStatus(document.signatureStatus)
                    const isDraft = document.signatureStatus === 'draft'
                    const isPendingOrSigned =
                      document.signatureStatus === 'pending' ||
                      document.signatureStatus === 'signed'

                    const dropdownItems = []

                    if (isDraft) {
                      dropdownItems.push({
                        label: i18n.t('it_management.people.actions.send_for_signature'),
                        icon: FileSigned,
                        onClick: async () => {
                          try {
                            // @ts-expect-error - Backend validates documentType is 'assign' or 'unassign'
                            const documentType: ItManagementValueObjectsItAssetDocumentTypeEnum =
                              document.documentType

                            await sendEmployeeDocumentTypeForSignature({
                              variables: {
                                companyId: Number(company.id),
                                documentType,
                                employeeId: employee.id,
                              },
                            })

                            app.setFlash({
                              type: 'correct',
                              message: i18n.t('it_management.people.send_signature.success'),
                            })

                            setLocalDocuments((prev) =>
                              prev.map((doc) =>
                                doc.id === document.id
                                  ? { ...doc, signatureStatus: 'pending' }
                                  : doc
                              )
                            )

                            pollingDocumentIdsRef.current.add(document.id)
                            startPolling(5000)

                            setTimeout(() => {
                              pollingDocumentIdsRef.current.delete(document.id)
                              if (pollingDocumentIdsRef.current.size === 0) {
                                stopPolling()
                              }
                            }, 60000)
                          } catch (error) {
                            app.setFlash({
                              type: 'wrong',
                              message: i18n.t('it_management.people.send_signature.error'),
                            })
                          }
                        },
                      })
                    }

                    if (isPendingOrSigned && document.documentId) {
                      dropdownItems.push({
                        label: i18n.t('it_management.asset_details.documents.view_document'),
                        icon: EyeVisible,
                        onClick: () => {
                          setSelectedDocumentId(String(document.documentId))
                        },
                      })
                    }

                    const documentTypeLabel =
                      document.documentType === 'assign'
                        ? i18n.t('it_management.people_tab.employee_details.assign')
                        : i18n.t('it_management.people_tab.employee_details.unassign')

                    const documentDisplayName =
                      document.documentFilename || 'Document (' + documentTypeLabel + ')'

                    const isLast = index === documents.length - 1
                    return (
                      <Box key={document.id}>
                        <Box padding="s16" flexDirection="row" alignItems="center" gap="s12">
                          {/* File icon and name - takes remaining space */}
                          <Box
                            flexDirection="row"
                            alignItems="center"
                            gap="s12"
                            style={{ flex: '1', minWidth: '0', overflow: 'hidden' }}
                          >
                            <Box flexNoShrink>
                              <F0AvatarFile
                                file={{
                                  name: documentDisplayName,
                                  type: 'application/pdf',
                                }}
                                size="md"
                              />
                            </Box>
                            <Box
                              style={{
                                minWidth: '0',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              <Text size="200">{documentDisplayName}</Text>
                            </Box>
                          </Box>

                          {/* Status - fixed position */}
                          <Box flexNoShrink>
                            <F0TagStatus
                              text={signatureStatus.text}
                              variant={signatureStatus.variant}
                            />
                          </Box>

                          {/* Actions Menu - fixed position */}
                          <Box flexNoShrink style={{ width: '40px' }}>
                            {dropdownItems.length > 0 && (
                              <Dropdown items={dropdownItems}>
                                <F0Button variant="tertiary" size="md" icon={Ellipsis} label="" />
                              </Dropdown>
                            )}
                          </Box>
                        </Box>
                        {/* Separator line between items */}
                        {!isLast && (
                          <>
                            <Box height="s1" background="grey300" />
                          </>
                        )}
                      </Box>
                    )
                  })}
                </Box>
              ) : (
                <Text.Normal color="grey600">No documents</Text.Normal>
              )}
            </Box>
          </Box>
        </OneModal.Content>
      </OneModal>
    </>
  )
}

export default EmployeeDetailsSidePanel
